// Dependencies
// -----------------------------------------------
import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import get from 'lodash/get';
import { TextDense } from '@directsoftware/ui-kit-web-admin';
import chargeDescription from '../../utils/charge-description';

const discountRateLabel = discount => {
  return discount.rate > 0 ? ` (${discount.rate}%)` : '';
};

const RenderDiscount = (discount, translate, currency, key) => {
  if (Number(discount.total_cents || 0) <= 0) {
    return null;
  }
  const rate = discountRateLabel(discount);
  return (
    <tr key={key}>
      <td>{`${discount.name || 'Discount'}${rate}`}</td>
      <td>
        <b className="discount">
          -
          {translate(`global.parsers.currency.${currency}`, {
            value: (Number(discount.total_cents || 0) / 100).toFixed(2)
          })}
        </b>
      </td>
    </tr>
  );
};

const RenderIncludedFeeLineItem = (fee, translate, currency, key) => {
  if (Number(fee.total_cents || 0) <= 0) {
    return null;
  }
  return (
    <tr className="line-item" key={key}>
      <td>
        <TextDense textColor="light-gray">
          {`${fee.name} ${translate(`cx.global.included_in_rate`)}`}
        </TextDense>
      </td>
      <td>
        <TextDense textColor="light-gray">
          {translate(`global.parsers.currency.${currency}`, {
            value: (Number(fee.total_cents || 0) / 100).toFixed(2)
          })}
        </TextDense>
      </td>
    </tr>
  );
};

const RenderTaxLineItem = (tax, translate, currency, key) => (
  <tr className="line-item" key={key}>
    <td>{tax.name || 'Taxes'}</td>
    <td>
      {translate(`global.parsers.currency.${currency}`, {
        value: (Number(tax.total_cents || 0) / 100).toFixed(2)
      })}
    </td>
  </tr>
);

const RenderLineItems = (pricing, nights, currency, translate) => {
  const fees = Array.isArray(pricing.fees) ? pricing.fees : [];
  const discounts = Array.isArray(pricing.discount) ? pricing.discount : [];
  const taxLineItems = Array.isArray(pricing.tax_line_items)
    ? pricing.tax_line_items
    : [];
  return (
    <>
      <tr className="line-item">
        <td>
          {translate(`global.parsers.currency_avg.${currency}`, {
            value: pricing.average_nightly_price
          })}{' '}
          ×{' '}
          {translate(
            `global.parsers.num_nights.${nights > 1 ? 'plural' : 'single'}`,
            { nights: nights }
          )}
        </td>
        <td>
          {translate(`global.parsers.currency.${currency}`, {
            value: pricing.room_rate
          })}
        </td>
      </tr>
      {fees
        .filter(fee => fee.additional_data?.included_in_base_rent)
        .map((fee, index) =>
          RenderIncludedFeeLineItem(
            fee,
            translate,
            currency,
            `included-${index}`
          )
        )}
      {discounts.map((discount, index) =>
        RenderDiscount(discount, translate, currency, `discount-${index}`)
      )}
      {fees.map(
        fee =>
          fee.total_cents > 0 && RenderFeeLineItem(fee, translate, currency)
      )}
      {taxLineItems.length > 0
        ? taxLineItems.map((tax, index) =>
            RenderTaxLineItem(tax, translate, currency, `tax-${index}`)
          )
        : pricing.taxes > 0 && (
            <tr className="line-item">
              <td>{translate(`cx.global.taxes`)}</td>
              <td>
                {translate(`global.parsers.currency.${currency}`, {
                  value: pricing.taxes
                })}
              </td>
            </tr>
          )}
    </>
  );
};

const RenderFeeLineItem = (fee, translate, currency) => {
  if (fee.additional_data.included_in_base_rent) {
    return null
  } else {
    return (
      <tr className="line-item">
        <td>{fee.name}</td>
        <td>
          {translate(`global.parsers.currency.${currency}`, {
            value: (fee.total_cents/100).toFixed(2)
          })}
        </td>
      </tr>
    );
  }
};

const RenderCharge = (charge, refunds, translate) => {
  const amount_charged = parseFloat(charge.amount_charged).toFixed(2);
  if (charge.status == 'charged' || charge.status == 'partially_refunded' && refunds?.length > 0 ) {
    return (
      <tr key={charge.id}>
        <td>
          <div>
            {moment(charge.created_at).format(
              get(this, 'props.displayFormat') === 'DD/MM/YYYY'
                ? 'D MMMM, YYYY'
                : 'MMMM D, YYYY'
            )}
          </div>
          <small>
          {chargeDescription(charge, translate)}
          </small>
        </td>
        <td>
          -{' '}
          {translate(`global.parsers.currency.${charge.currency}`, {
            value: amount_charged
          })}
        </td>
      </tr>
    );
  } else {
    return null;
  }
};

const RenderRefund = (refunds, currency, translate) => {
  if (refunds?.length > 0 ) {
    return refunds.map(refund => (
      <tr className="refund">
        <td>
          <div>
            Refund
          </div>
        </td>
        <td>
          -{' '}
          {translate(`global.parsers.currency.${currency}`, {
            value: parseFloat(refund).toFixed(2)
          })}
        </td>
      </tr>)
    );
  } else {
    return null;
  }
};

const InfoPricing = ({
  translate,
  booking,
  nights,
  currency,
  charges,
  pricing,
  refunds,
  paymentDueToday,
  paymentBalanceDueDate
}) => {
  const total = (
    Math.floor(parseFloat(booking.price_total) * 100) / 100
  ).toFixed(2);
  const paid = (Math.floor(parseFloat(booking.price_paid) * 100) / 100).toFixed(
    2
  );
  let remaining = (total - paid).toFixed(2);
  if (remaining < 0) {
    remaining = (0).toFixed(2);
  }
  const discounts = Array.isArray(pricing?.discount) ? pricing.discount : [];
  const discountTotalCents = discounts.reduce((totalCents, discount) => {
    return totalCents + Number(discount.total_cents || 0);
  }, 0);
  return (
    <>
      <section className="receipt-info-section">
        <div className="receipt-info-subsection">
          <h3>{translate(`cx.global.payment_information`)}</h3>
          <table className="receipt-pricing-breakdown">
            <tbody>
              {pricing &&
                RenderLineItems(pricing, nights, currency, translate)}

              <tr className="total">
                <td>{translate(`cx.global.total`)}</td>
                <td>
                  {translate(`global.parsers.currency.${currency}`, {
                    value: total
                  })}
                </td>
              </tr>
              {discountTotalCents > 0 && (
                <tr className="line-item">
                  <td>{translate(`cx.global.you_saved`)}</td>
                  <td>
                    <b className="discount">
                      {translate(`global.parsers.currency.${currency}`, {
                        value: (discountTotalCents / 100).toFixed(2)
                      })}
                    </b>
                  </td>
                </tr>
              )}
              {Number(paymentDueToday || 0) > 0 && (
                <tr className="line-item">
                  <td>{translate(`cx.global.immediate_payment`)}</td>
                  <td>
                    {translate(`global.parsers.currency.${currency}`, {
                      value: Number(paymentDueToday).toFixed(2)
                    })}
                  </td>
                </tr>
              )}
              {charges.map(charge => RenderCharge(charge, refunds, translate))}
              {RenderRefund(refunds, currency, translate)}
              <tr className="remaining">
                <td>{translate(`cx.global.remaining`)}</td>
                <td>
                  {translate(`global.parsers.currency.${currency}`, {
                    value: remaining
                  })}
                  {Number(remaining) > 0 && paymentBalanceDueDate && (
                    <TextDense textColor="light-gray">
                      {translate(`cx.global.balance_due_date`, {
                        date: paymentBalanceDueDate
                      })}
                    </TextDense>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
};

InfoPricing.propTypes = {
  translate: PropTypes.func.isRequired,
  booking: PropTypes.object.isRequired,
  nights: PropTypes.number,
  currency: PropTypes.string,
  charges: PropTypes.array,
  pricing: PropTypes.object,
  refunds: PropTypes.array,
  paymentDueToday: PropTypes.number,
  paymentBalanceDueDate: PropTypes.string
};

InfoPricing.defaultProps = {
  nights: null,
  currency: 'USD',
  charges: [],
  pricing: null,
  refunds: [],
  paymentDueToday: null,
  paymentBalanceDueDate: null
};

export default InfoPricing;
